import { create } from 'zustand';
import type { Event, EventStats, PaginatedEventsResponse } from '../types';

interface EventState {
  // Data
  events: Event[];
  activeEvents: Event[];
  upcomingEvents: Event[];
  currentEvent: Event | null;
  eventStats: EventStats | null;
  
  // UI State
  isLoading: boolean;
  error: string | null;
  
  // Pagination
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | null;
  currentPage: number;
  
  // Filters
  searchQuery: string;
  activeFilter: boolean | null;
}

interface EventActions {
  // Events
  setEvents: (response: PaginatedEventsResponse) => void;
  setActiveEvents: (response: PaginatedEventsResponse) => void;
  setUpcomingEvents: (response: PaginatedEventsResponse) => void;
  setCurrentEvent: (event: Event | null) => void;
  addEvent: (event: Event) => void;
  updateEvent: (eventId: number, updates: Partial<Event>) => void;
  
  // Stats
  setEventStats: (stats: EventStats) => void;
  
  // UI State
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Pagination
  setCurrentPage: (page: number) => void;
  
  // Filters
  setSearchQuery: (query: string) => void;
  setActiveFilter: (active: boolean | null) => void;
  clearFilters: () => void;
  
  // Actions
  clearEvents: () => void;
  clearCurrentEvent: () => void;
}

type EventStore = EventState & EventActions;

const initialState: EventState = {
  events: [],
  activeEvents: [],
  upcomingEvents: [],
  currentEvent: null,
  eventStats: null,
  isLoading: false,
  error: null,
  pagination: null,
  currentPage: 1,
  searchQuery: '',
  activeFilter: null,
};

export const useEventStore = create<EventStore>((set) => ({
  ...initialState,
  
  // Events
  setEvents: (response) => {
    set({
      events: response.events,
      pagination: response.pagination,
      isLoading: false,
      error: null,
    });
  },
  
  setActiveEvents: (response) => {
    set({
      activeEvents: response.events,
      isLoading: false,
      error: null,
    });
  },
  
  setUpcomingEvents: (response) => {
    set({
      upcomingEvents: response.events,
      isLoading: false,
      error: null,
    });
  },
  
  setCurrentEvent: (event) => {
    set({ currentEvent: event });
  },
  
  addEvent: (event) => {
    set((state) => ({
      events: [event, ...state.events],
      activeEvents: event.isActive 
        ? [event, ...state.activeEvents]
        : state.activeEvents,
    }));
  },
  
  updateEvent: (eventId, updates) => {
    set((state) => ({
      events: state.events.map(event => 
        event.id === eventId ? { ...event, ...updates } : event
      ),
      activeEvents: state.activeEvents.map(event => 
        event.id === eventId ? { ...event, ...updates } : event
      ),
      upcomingEvents: state.upcomingEvents.map(event => 
        event.id === eventId ? { ...event, ...updates } : event
      ),
      currentEvent: state.currentEvent?.id === eventId 
        ? { ...state.currentEvent, ...updates }
        : state.currentEvent,
    }));
  },
  
  // Stats
  setEventStats: (stats) => {
    set({ eventStats: stats });
  },
  
  // UI State
  setLoading: (loading) => {
    set({ isLoading: loading });
  },
  
  setError: (error) => {
    set({ error, isLoading: false });
  },
  
  clearError: () => {
    set({ error: null });
  },
  
  // Pagination
  setCurrentPage: (page) => {
    set({ currentPage: page });
  },
  
  // Filters
  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },
  
  setActiveFilter: (active) => {
    set({ activeFilter: active });
  },
  
  clearFilters: () => {
    set({
      searchQuery: '',
      activeFilter: null,
    });
  },
  
  // Actions
  clearEvents: () => {
    set({
      events: [],
      pagination: null,
      currentPage: 1,
    });
  },
  
  clearCurrentEvent: () => {
    set({ currentEvent: null });
  },
}));